/**
 * Email Test Modal Component
 * Feature: System Administration Interface (001-admin-settings)
 */
import React, { useState } from 'react';
import { X, Send, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { testEmailConfiguration } from '../../api/email-config';
import type { EmailTestRequest, EmailTestResponse } from '../../types/email-config';
import Button from '../Button';

interface EmailTestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EmailTestModal: React.FC<EmailTestModalProps> = ({ isOpen, onClose }) => {
  const [recipientEmail, setRecipientEmail] = useState<string>('');
  const [testMessage, setTestMessage] = useState<string>('');
  const [isSending, setIsSending] = useState<boolean>(false);
  const [testResult, setTestResult] = useState<EmailTestResponse | null>(null);

  const handleSendTest = async () => {
    if (!recipientEmail || !recipientEmail.includes('@')) {
      setTestResult({
        success: false,
        message: 'Please enter a valid email address',
        error_details: 'Invalid email format'
      });
      return;
    }

    setIsSending(true);
    setTestResult(null);

    try {
      const request: EmailTestRequest = {
        recipient_email: recipientEmail,
        test_message: testMessage || null
      };

      const response = await testEmailConfiguration(request);
      setTestResult(response);

      if (response.success) {
        // Auto-close after 3 seconds on success
        setTimeout(() => {
          handleClose();
        }, 3000);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send test email';
      setTestResult({
        success: false,
        message: 'Test email failed',
        error_details: errorMessage
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    setRecipientEmail('');
    setTestMessage('');
    setTestResult(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Send className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Test Email Configuration
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Recipient Email */}
          <div>
            <label
              htmlFor="recipient-email"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Recipient Email <span className="text-red-500">*</span>
            </label>
            <input
              id="recipient-email"
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="test@example.com"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              disabled={isSending}
            />
          </div>

          {/* Custom Test Message */}
          <div>
            <label
              htmlFor="test-message"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Custom Message (Optional)
            </label>
            <textarea
              id="test-message"
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
              placeholder="Enter a custom test message or leave blank for default"
              rows={3}
              maxLength={500}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
              disabled={isSending}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {testMessage.length}/500 characters
            </p>
          </div>

          {/* Test Result */}
          {testResult && (
            <div
              className={`p-4 rounded-lg flex items-start gap-3 ${
                testResult.success
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                  : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
              }`}
            >
              {testResult.success ? (
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <p
                  className={`font-medium ${
                    testResult.success
                      ? 'text-green-800 dark:text-green-300'
                      : 'text-red-800 dark:text-red-300'
                  }`}
                >
                  {testResult.message}
                </p>
                {testResult.delivery_time_ms && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Delivery time: {testResult.delivery_time_ms}ms
                  </p>
                )}
                {testResult.error_details && (
                  <p className="text-sm text-red-700 dark:text-red-400 mt-2">
                    {testResult.error_details}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" onClick={handleClose} disabled={isSending}>
            {testResult?.success ? 'Close' : 'Cancel'}
          </Button>
          <Button
            variant="primary"
            onClick={handleSendTest}
            disabled={isSending || !recipientEmail}
          >
            {isSending ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send Test Email
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EmailTestModal;
